
import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { AdminService } from '../../../application/services/AdminService';
import { useToast } from '../../../context/ToastContext';

const EmpresaForm = ({ onSuccess, onCancel, initialData }) => {
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();
    const [step, setStep] = useState(1); // 1: Empresa, 2: Admin
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        empresa: {
            nombre: '',
            ruc: '',
            telefono: '',
            emailInstitucional: '',
            provincia: '',
            canton: '',
            parroquia: '',
            direccion: '',
            logotipo: '',
            dominio: '',
            mision: '',
            vision: '',
            historia: ''
        },
        admin: {
            nombre: '',
            cedula: '',
            email: '',
            usuario: '',
            password: ''
        }
    });

    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                empresa: {
                    ...prev.empresa,
                    nombre: initialData.nombre || '',
                    razonSocial: initialData.razonSocial || '',
                    nombreComercial: initialData.nombreComercial || '',
                    ruc: initialData.ruc || initialData.nit || '',
                    acuerdoMinisterial: initialData.acuerdoMinisterial || '',
                    fechaAcuerdo: initialData.fechaAcuerdo || '',
                    telefono: initialData.telefono || '',
                    emailInstitucional: initialData.emailInstitucional || initialData.email || '',
                    provincia: initialData.provincia || '',
                    canton: initialData.canton || '',
                    parroquia: initialData.parroquia || '',
                    comunidad: initialData.comunidad || '',
                    direccion: initialData.direccion || '',
                    logotipo: initialData.logotipo || '',
                    dominio: initialData.dominio || '',
                    mision: initialData.configuracion?.mision || '',
                    vision: initialData.configuracion?.vision || '',
                    historia: initialData.configuracion?.historia || ''
                }
            }));
        }
    }, [initialData]);

    const handleChange = (section, field, value) => {
        // Al modificar cualquier campo, limpiamos los errores para mejorar la UX
        if (error) setError('');
        
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            if (initialData) {
                // 1. Detectar si hubo cambios
                const hasChanges = Object.keys(formData.empresa).some(key => {
                    let newValue = formData.empresa[key];
                    let originalValue = initialData[key];

                    // Normalizar valores para comparación
                    if (newValue === null || newValue === undefined) newValue = '';
                    if (originalValue === null || originalValue === undefined) originalValue = '';
                    
                    // Manejo especial fechas (YYYY-MM-DD vs ISO)
                    if (key === 'fechaAcuerdo') {
                         if (!newValue && !originalValue) return false;
                         const newDate = newValue ? newValue.split('T')[0] : '';
                         const origDate = originalValue ? originalValue.split('T')[0] : '';
                         return newDate !== origDate;
                    }

                    return String(newValue).trim() !== String(originalValue).trim();
                });

                if (!hasChanges) {
                    toast.success('Empresa actualizada exitosamente'); // Mensaje "fake" para UX
                    onSuccess();
                    return;
                }

                // 2. Sanitizar datos antes de enviar
                const dataToSend = { ...formData.empresa };
                // Si fecha es string vacío, enviar null para evitar error "invalid input syntax for type date"
                if (!dataToSend.fechaAcuerdo) {
                    dataToSend.fechaAcuerdo = null;
                }
                
                // Asegurar que mision, vision e historia van en configuracion
                dataToSend.configuracion = {
                    ...(initialData?.configuracion || {}),
                    mision: dataToSend.mision,
                    vision: dataToSend.vision,
                    historia: dataToSend.historia
                };
                delete dataToSend.mision;
                delete dataToSend.vision;
                delete dataToSend.historia;

                 await AdminService.updateEmpresa(initialData.id, dataToSend);
            } else {
                 const dataToCreate = { ...formData };
                 // Mapear configuracion para create
                 dataToCreate.empresa.configuracion = {
                    mision: dataToCreate.empresa.mision,
                    vision: dataToCreate.empresa.vision,
                    historia: dataToCreate.empresa.historia
                 };
                 delete dataToCreate.empresa.mision;
                 delete dataToCreate.empresa.vision;
                 delete dataToCreate.empresa.historia;
                 
                 await AdminService.createEmpresa(dataToCreate);
            }
            onSuccess();
        } catch (err) {
            console.error('Error en handleSubmit:', err);
            
            // Extraer mensaje de error de forma robusta
            let errorMsg = 'Error al procesar';
            if (typeof err === 'string') {
                errorMsg = err;
            } else if (err?.message) {
                errorMsg = err.message;
            } else if (err?.error) {
                errorMsg = err.error;
            }
            
            setError(errorMsg);
            
            // Intento de mapear errores de validación del backend a campos
            const lowerError = errorMsg.toLowerCase();
            if (lowerError.includes('ruc')) {
                document.getElementById('empresa_ruc')?.focus();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Helper para verificar si un campo tiene error basado en el mensaje global
    // Esto es temporal hasta que el backend devuelva errores estructurados JSON.
    const hasError = (fieldKeywords) => {
        if (!error) return false;
        return fieldKeywords.some(keyword => error.toLowerCase().includes(keyword.toLowerCase()));
    };

    // En edición, solo mostramos el paso 1 (Empresa)
    const isEditing = !!initialData;

    return (
        <form onSubmit={handleSubmit}>
            {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 text-red-700">
                    <p className="font-bold">Error encontrado:</p>
                    <p>{error}</p>
                </div>
            )}

            {step === 1 ? (
                <div className="space-y-1">
                    <div className="grid grid-cols-2 gap-2">
                        <Input 
                            label="Nombre de la Junta" 
                            value={formData.empresa.nombre} 
                            onChange={e => handleChange('empresa', 'nombre', e.target.value)} 
                            required 
                            error={hasError(['nombre']) ? 'Verificar nombre' : null}
                            className="mb-1"
                        />
                         <Input 
                            label="Razón Social" 
                            value={formData.empresa.razonSocial || ''} 
                            onChange={e => handleChange('empresa', 'razonSocial', e.target.value)} 
                            placeholder="Ej. Junta Administradora..."
                            className="mb-1"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <Input 
                            label="RUC" 
                            value={formData.empresa.ruc} 
                            onChange={e => handleChange('empresa', 'ruc', e.target.value)} 
                            required 
                            error={hasError(['ruc', 'nit']) ? 'RUC duplicado o inválido' : null}
                            className="mb-1"
                        />
                         <Input 
                            label="Nombre Comercial" 
                            value={formData.empresa.nombreComercial || ''} 
                            onChange={e => handleChange('empresa', 'nombreComercial', e.target.value)} 
                            className="mb-1"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <Input 
                            label="Acuerdo Ministerial" 
                            value={formData.empresa.acuerdoMinisterial || ''} 
                            onChange={e => handleChange('empresa', 'acuerdoMinisterial', e.target.value)} 
                            className="mb-1"
                        />
                         <Input 
                            label="Fecha Acuerdo" 
                            type="date"
                            value={formData.empresa.fechaAcuerdo ? formData.empresa.fechaAcuerdo.split('T')[0] : ''} 
                            onChange={e => handleChange('empresa', 'fechaAcuerdo', e.target.value)} 
                            className="mb-1"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <Input 
                            label="Teléfono" 
                            value={formData.empresa.telefono} 
                            onChange={e => handleChange('empresa', 'telefono', e.target.value)} 
                            className="mb-1"
                        />
                        <Input 
                            label="Email Institucional" 
                            type="email" 
                            value={formData.empresa.emailInstitucional} 
                            onChange={e => handleChange('empresa', 'emailInstitucional', e.target.value)} 
                            required 
                            error={hasError(['email_institucional', 'email']) && step === 1 ? 'Email ya registrado' : null}
                            className="mb-1"
                        />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                        <Input label="Provincia" value={formData.empresa.provincia} onChange={e => handleChange('empresa', 'provincia', e.target.value)} className="mb-1" />
                        <Input label="Cantón" value={formData.empresa.canton} onChange={e => handleChange('empresa', 'canton', e.target.value)} className="mb-1" />
                        <Input label="Parroquia" value={formData.empresa.parroquia || ''} onChange={e => handleChange('empresa', 'parroquia', e.target.value)} className="mb-1" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                         <Input label="Comunidad / Barrio" value={formData.empresa.comunidad || ''} onChange={e => handleChange('empresa', 'comunidad', e.target.value)} className="mb-1" />
                         <Input label="Dirección Exacta" value={formData.empresa.direccion} onChange={e => handleChange('empresa', 'direccion', e.target.value)} className="mb-1" />
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Identidad Visual y Web (Opcional)</h4>
                        <div className="grid grid-cols-2 gap-2">
                            <Input 
                                label="Dominio Personalizado" 
                                value={formData.empresa.dominio} 
                                onChange={e => handleChange('empresa', 'dominio', e.target.value)} 
                                placeholder="ej. agua-quito.com"
                                className="mb-1"
                            />
                            <Input 
                                label="URL del Logotipo" 
                                value={formData.empresa.logotipo} 
                                onChange={e => handleChange('empresa', 'logotipo', e.target.value)} 
                                placeholder="https://..."
                                className="mb-1"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <div className="flex flex-col">
                                <label className="text-xs font-medium text-gray-700 mb-1">Misión</label>
                                <textarea 
                                    className="border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500" 
                                    rows="3" 
                                    value={formData.empresa.mision} 
                                    onChange={e => handleChange('empresa', 'mision', e.target.value)} 
                                    placeholder="Misión de la junta..."
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs font-medium text-gray-700 mb-1">Visión</label>
                                <textarea 
                                    className="border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500" 
                                    rows="3" 
                                    value={formData.empresa.vision} 
                                    onChange={e => handleChange('empresa', 'vision', e.target.value)} 
                                    placeholder="Visión de la junta..."
                                />
                            </div>
                        </div>
                        <div className="flex flex-col mt-2">
                            <label className="text-xs font-medium text-gray-700 mb-1">Historia</label>
                            <textarea 
                                className="border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500" 
                                rows="3" 
                                value={formData.empresa.historia} 
                                onChange={e => handleChange('empresa', 'historia', e.target.value)} 
                                placeholder="Breve historia de la junta..."
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-1">
                    <Input label="Admin" value={formData.admin.nombre} onChange={e => handleChange('admin', 'nombre', e.target.value)} required className="mb-1" />
                    <div className="grid grid-cols-2 gap-2">
                        <Input 
                            label="Cédula" 
                            value={formData.admin.cedula} 
                            onChange={e => handleChange('admin', 'cedula', e.target.value)} 
                            required 
                            error={hasError(['cedula']) ? 'Cédula ya registrada' : null}
                            className="mb-1"
                        />
                        <Input 
                            label="Email" 
                            type="email" 
                            value={formData.admin.email} 
                            onChange={e => handleChange('admin', 'email', e.target.value)} 
                            required 
                            error={hasError(['email']) && step === 2 ? 'Email ya registrado' : null}
                            className="mb-1"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Input 
                            label="Nombre Usuario" 
                            value={formData.admin.usuario} 
                            onChange={e => handleChange('admin', 'usuario', e.target.value)} 
                            required 
                            placeholder="ej. admin_junta" 
                            error={hasError(['usuario']) ? 'Usuario ya existe' : null}
                            className="mb-1"
                        />
                        <Input label="Contraseña Temporal" type="password" value={formData.admin.password} onChange={e => handleChange('admin', 'password', e.target.value)} required className="mb-1" />
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex justify-end gap-2 pt-2 border-t border-gray-100">
                 {step === 1 ? (
                    <>
                         <Button type="button" variant="secondary" onClick={onCancel}>
                            Cancelar
                        </Button>
                        {/* Si estamos editando, mostramos Guardar Directamente. Si creando, Siguiente */}
                        {isEditing ? (
                            <Button type="submit" isLoading={isLoading} className="bg-blue-600 hover:bg-blue-700">
                                Guardar Cambios
                            </Button>
                        ) : (
                            <Button type="button" onClick={() => setStep(2)}>
                                Siguiente paso
                            </Button>
                        )}
                    </>
                 ) : (
                    <>
                        <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                            Atrás
                        </Button>
                        <Button type="submit" isLoading={isLoading} className="bg-green-600 hover:bg-green-700">
                            Confirmar y Crear
                        </Button>
                    </>
                 )}
            </div>
        </form>
    );
};

export default EmpresaForm;
